import { MessageCircle, ThumbsDown, ThumbsUp } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface FeedbackActionsProps {
  onHelpful: () => void;
  onUnhelpful: () => void;
  onFeedback: () => void;
  isHelpfulClicked?: boolean;
  isUnhelpfulClicked?: boolean;
  hasFeedbackSubmission?: boolean;
  /** Disables Helpful and Unhelpful, for example while a rating saves. */
  ratingDisabled?: boolean;
  /** What the actions rate, as named in their tooltips. */
  subject?: string;
  className?: string;
}

/** Helpful, Unhelpful, and Feedback buttons shared by Research Assistant answers and Code Library comparisons. */
function FeedbackActions({
  onHelpful,
  onUnhelpful,
  onFeedback,
  isHelpfulClicked,
  isUnhelpfulClicked,
  hasFeedbackSubmission,
  ratingDisabled = false,
  subject = "response",
  className,
}: FeedbackActionsProps) {
  return (
    <div className={cn("flex gap-2 ml-2", className)}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onHelpful}
              disabled={ratingDisabled}
              aria-pressed={Boolean(isHelpfulClicked)}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
            >
              <ThumbsUp className="h-4 w-4" fill={isHelpfulClicked ? "currentColor" : "none"} />
              <span className="sr-only">Helpful</span>
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Helpful {subject}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onUnhelpful}
              disabled={ratingDisabled}
              aria-pressed={Boolean(isUnhelpfulClicked)}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
            >
              <ThumbsDown className="h-4 w-4" fill={isUnhelpfulClicked ? "currentColor" : "none"} />
              <span className="sr-only">Unhelpful</span>
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Unhelpful {subject}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onFeedback}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
            >
              <MessageCircle className="h-4 w-4" fill={hasFeedbackSubmission ? "currentColor" : "none"} />
              <span className="sr-only">Feedback</span>
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Give feedback on {subject}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

export default FeedbackActions;
